/**
 * AI 写作练习页面
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { AIWritingCard, TopicInfo, PracticeMode } from './types';
import { getTopics, getRandomCardByTopic, isTranslationCard, isArgumentCard } from './dataService';
import { TranslationPractice } from './TranslationPractice';
import { ArgumentPractice } from './ArgumentPractice';
import type { SrsFeedback } from '../../db/indexedDb';
import { reviewCard } from '../../srs/reviewDb';

type PageState = 'topics' | 'practice';

export function AIWritingPage() {
  const [pageState, setPageState] = useState<PageState>('topics');
  const [topics, setTopics] = useState<TopicInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTopic, setSelectedTopic] = useState<TopicInfo | null>(null);
  const [selectedMode, setSelectedMode] = useState<PracticeMode>('translation');
  const [currentCard, setCurrentCard] = useState<AIWritingCard | null>(null);
  const [evaluated, setEvaluated] = useState(false); // 评判是否完成
  const [saving, setSaving] = useState(false); // 评分提交中
  const [error, setError] = useState<string | null>(null);

  // 加载话题列表
  useEffect(() => {
    getTopics()
      .then(setTopics)
      .catch((err) => {
        console.error('[ai-writing] Failed to load topics:', err);
        setError('加载话题失败，请刷新重试');
      })
      .finally(() => setLoading(false));
  }, []);

  // 随机开始（不选话题）
  const startRandom = async () => {
    const { getRandomCard } = await import('./dataService');
    const card = await getRandomCard();
    if (card) {
      setSelectedTopic(null);
      setSelectedMode(card.type as PracticeMode);
      setCurrentCard(card);
      setPageState('practice');
    }
  };

  // 开始练习（指定话题）
  const startPractice = async (topic: TopicInfo, mode: PracticeMode) => {
    setSelectedTopic(topic);
    setSelectedMode(mode);
    const card = await getRandomCardByTopic(topic.en, mode);
    if (card) {
      setCurrentCard(card);
      setPageState('practice');
    }
  };

  // 处理 SRS 评分并加载下一题
  const handleRating = async (feedback: SrsFeedback) => {
    if (saving) return;
    setSaving(true);
    
    // 记录 SRS 评分
    if (currentCard) {
      try {
        await reviewCard(currentCard.id, feedback);
        console.log(`[ai-writing] Rated ${currentCard.id} as ${feedback}`);
      } catch (err) {
        console.error('[ai-writing] Failed to record rating:', err);
      }
    }

    // 重置评判状态
    setEvaluated(false);

    // 加载下一题
    const { getRandomCard } = await import('./dataService');
    if (!selectedTopic) {
      const card = await getRandomCard();
      if (card) {
        setSelectedMode(card.type as PracticeMode);
        setCurrentCard(card);
      }
    } else {
      const card = await getRandomCardByTopic(selectedTopic.en, selectedMode);
      if (card) setCurrentCard(card);
    }
    
    setSaving(false);
  };

  // 返回话题列表
  const handleBack = () => {
    setPageState('topics');
    setCurrentCard(null);
  };

  if (loading) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center' }}>
        <div className="loading-spinner"><span></span></div>
        <p style={{ marginTop: '16px', color: 'var(--text-secondary)' }}>加载中...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center' }}>
        <p style={{ color: 'var(--accent-red)' }}>{error}</p>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: '16px',
            padding: '10px 20px',
            background: 'var(--accent-cyan)',
            border: '2px solid var(--border-color)',
            cursor: 'pointer',
          }}
        >
          刷新页面
        </button>
      </div>
    );
  }

  // 话题选择页面
  if (pageState === 'topics') {
    return (
      <div className="session-layout">
        <header className="session-header">
          <Link to="/today" className="session-back">←</Link>
          <span className="session-title">写作</span>
          <span className="session-count"></span>
        </header>
        <div className="session-content" style={{ padding: '20px', overflowY: 'auto' }}>

        {/* 随机开始按钮 */}
        <button
          onClick={startRandom}
          style={{
            width: '100%',
            padding: '16px',
            marginBottom: '20px',
            background: 'var(--accent-cyan)',
            border: '2px solid var(--border-color)',
            fontWeight: 700,
            fontSize: '14px',
            cursor: 'pointer',
            color: 'var(--text-primary)',
          }}
        >
          随机开始 →
        </button>

        {/* 或选择话题 */}
        <div style={{ 
          fontSize: '12px', 
          color: 'var(--text-secondary)', 
          marginBottom: '12px',
          textAlign: 'center',
        }}>
          或选择话题
        </div>

        {/* 练习模式切换 */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          <button
            onClick={() => setSelectedMode('translation')}
            style={{
              flex: 1,
              padding: '10px',
              background: selectedMode === 'translation' ? 'var(--accent-cyan)' : 'var(--bg-card)',
              border: '2px solid var(--border-color)',
              fontWeight: 700,
              fontSize: '12px',
              cursor: 'pointer',
              color: 'var(--text-primary)',
            }}
          >
            句子翻译
          </button>
          <button
            onClick={() => setSelectedMode('argument')}
            style={{
              flex: 1,
              padding: '10px',
              background: selectedMode === 'argument' ? 'var(--accent-cyan)' : 'var(--bg-card)',
              border: '2px solid var(--border-color)',
              fontWeight: 700,
              fontSize: '12px',
              cursor: 'pointer',
              color: 'var(--text-primary)',
            }}
          >
            要点组句
          </button>
        </div>

        {/* 话题列表 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {topics.map((topic) => (
            <button
              key={topic.en}
              onClick={() => startPractice(topic, selectedMode)}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px',
                background: 'var(--bg-card)',
                border: '2px solid var(--border-color)',
                cursor: 'pointer',
                textAlign: 'left',
                color: 'var(--text-primary)',
              }}
            >
              <div>
                <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text-primary)' }}>{topic.cn}</div>
                <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                  {topic.en}
                </div>
              </div>
              <div style={{
                fontSize: '11px',
                color: 'var(--text-secondary)',
                background: 'var(--accent-yellow)',
                padding: '2px 6px',
                border: '1px solid var(--border-color)',
              }}>
                {selectedMode === 'translation' ? topic.translationCount : topic.argumentCount}
              </div>
            </button>
          ))}
        </div>
        </div>
      </div>
    );
  }

  // 练习页面
  if (pageState === 'practice' && currentCard) {
    const modeLabel = selectedMode === 'translation' ? '翻译' : '组句';
    const title = selectedTopic ? selectedTopic.en.toUpperCase() : 'RANDOM';
    return (
      <div className="session-layout">
        <header className="session-header">
          <Link to="/ai-writing" onClick={(e) => { e.preventDefault(); handleBack(); }} className="session-back">←</Link>
          <span className="session-title">{title}</span>
          <span className="session-count">{modeLabel}</span>
        </header>

        <div className="session-content">
          {isTranslationCard(currentCard) && (
            <TranslationPractice card={currentCard} onEvaluated={setEvaluated} />
          )}
          {isArgumentCard(currentCard) && (
            <ArgumentPractice card={currentCard} onEvaluated={setEvaluated} />
          )}
        </div>

        {/* 底部评分按钮 */}
        <div className="session-footer">
          <div className="feedback-group">
            <button
              className="feedback-btn again"
              onClick={() => handleRating('again')}
              disabled={!evaluated || saving}
            >
              <span>AGAIN</span>
              <span className="interval">&lt;1d</span>
            </button>
            <button
              className="feedback-btn hard"
              onClick={() => handleRating('hard')}
              disabled={!evaluated || saving}
            >
              <span>HARD</span>
              <span className="interval">1d</span>
            </button>
            <button
              className="feedback-btn good"
              onClick={() => handleRating('good')}
              disabled={!evaluated || saving}
            >
              <span>GOOD</span>
              <span className="interval">3d</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
